import { styled } from "@mui/material/styles";
import { ReasoningIcon, Spinner, StatusDot, usePresence } from "@UI";
import { getToolName, isToolUIPart } from "ai";
import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWordExecCards } from "../../hooks/clientTools/wordExec";
import { useChatSessionStore } from "../../store/chatSessionStore";
import { isClientReadTool, isMutatingTool, toolIcon, toolStatusTone } from "../utils/tool-utils";
import { ReasoningPart, splitReasoning } from "./ReasoningPart";
import { labelName, reveal, StepChevron, toolStepLabel, ToolPart } from "./ToolPart";
const MAX_ICONS = 3;
const MS_IN_S = 1000;
const S_IN_MIN = 60;
const Feed = styled("div")({
    marginLeft: "calc(-1 * var(--j-sp-1))"
});
const Trigger = styled("button")({
    alignSelf: "flex-start",
    maxWidth: "100%",
    minHeight: "var(--j-row)",
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)",
    padding: 0,
    border: 0,
    borderRadius: "var(--j-r-sm)",
    background: "transparent",
    fontSize: "var(--j-fs-sm)",
    lineHeight: "var(--j-lh-md)",
    color: "var(--j-ink-2)",
    textAlign: "left",
    cursor: "pointer",
    "& > .feed-icons": {
        display: "flex",
        gap: "var(--j-sp-1-5)",
        color: "var(--j-ink-3)"
    },
    "& > .feed-summary": {
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        transition: "color var(--j-dur-fade)"
    },
    "&:hover > .feed-summary": {
        color: "var(--j-ink)"
    },
    "& .feed-mid": {
        opacity: 0.6,
        margin: "0 var(--j-sp-1)"
    },
    "& .feed-failed": {
        color: "var(--j-err)"
    },
    "& .feed-time": {
        fontVariantNumeric: "tabular-nums"
    },
    "& .step-target": {
        fontWeight: 500
    }
});
const Rail = styled("div")({
    ...reveal,
    margin: "0 0 0 6px",
    paddingLeft: "var(--j-sp-3)",
    borderLeft: "1px solid var(--j-line)",
    display: "flex",
    flexDirection: "column"
});
function formatDuration(seconds) {
    if (seconds < S_IN_MIN) return `${seconds}s`;
    return `${Math.floor(seconds / S_IN_MIN)}m ${String(seconds % S_IN_MIN).padStart(2, "0")}s`;
}
function useElapsed(isActive) {
    const [startedAt, setStartedAt] = useState(isActive ? Date.now() : null);
    const [duration, setDuration] = useState(null);
    const [now, setNow] = useState(()=>Date.now());
    useEffect(()=>{
        if (isActive && startedAt === null) setStartedAt(Date.now());
        if (!isActive && startedAt !== null) {
            setDuration((total)=>(total ?? 0) + Math.ceil((Date.now() - startedAt) / MS_IN_S));
            setStartedAt(null);
        }
    }, [
        isActive,
        startedAt
    ]);
    useEffect(()=>{
        if (!isActive) return undefined;
        const tick = setInterval(()=>setNow(Date.now()), MS_IN_S);
        return ()=>clearInterval(tick);
    }, [
        isActive
    ]);
    return startedAt === null ? duration : (duration ?? 0) + Math.floor((now - startedAt) / MS_IN_S);
}
function Mid() {
    return /*#__PURE__*/ React.createElement("span", {
        className: "feed-mid",
        "aria-hidden": true
    }, "·");
}
export function ActivityFeed({ parts: allParts, isActive, ctx }) {
    const { t, i18n } = useTranslation();
    const execCards = useWordExecCards((state)=>state.cards);
    const parts = allParts.filter(({ part })=>{
        const status = isToolUIPart(part) ? execCards[part.toolCallId]?.status : undefined;
        return status !== "waiting" && status !== "running";
    });
    const listId = useId();
    const [isOpen, setIsOpen] = useState(isActive);
    const rail = usePresence(isOpen);
    const wasActive = useRef(isActive);
    const applyingToolIds = useChatSessionStore((state)=>state.applyingToolIds);
    const isApplying = parts.some(({ part })=>{
        const { toolCallId } = part;
        return toolCallId !== undefined && applyingToolIds.includes(toolCallId);
    });
    const elapsed = useElapsed(isActive || isApplying);
    useEffect(()=>{
        if (wasActive.current === isActive) return;
        wasActive.current = isActive;
        setIsOpen(isActive);
    }, [
        isActive
    ]);
    const isSpinning = isActive && !isOpen;
    const { onStepsSpinning } = ctx;
    useEffect(()=>{
        if (!isSpinning) return undefined;
        onStepsSpinning?.(true);
        return ()=>onStepsSpinning?.(false);
    }, [
        isSpinning,
        onStepsSpinning
    ]);
    const errorCount = parts.filter(({ part })=>isToolUIPart(part) && toolStatusTone(part) === "error").length;
    const icons = parts.reduce((list, { part, index })=>{
        const Icon = isToolUIPart(part) ? toolIcon(labelName(part)) : ReasoningIcon;
        return list.length < MAX_ICONS && !list.some((entry)=>entry.Icon === Icon) ? [
            ...list,
            {
                Icon,
                key: index
            }
        ] : list;
    }, []);
    const only = parts.length === 1 && !isActive && errorCount === 0 ? parts[0].part : null;
    const singleLabel = only && isToolUIPart(only) ? toolStepLabel(only, t, (key)=>i18n.exists(key), ctx.isStreaming) : only?.type === "reasoning" ? splitReasoning(only.text, i18n.language).heading || t("chat.activity.reasoning") : null;
    const leading = isSpinning ? /*#__PURE__*/ React.createElement(Spinner, null) : errorCount > 0 ? /*#__PURE__*/ React.createElement(StatusDot, {
        status: "warn"
    }) : /*#__PURE__*/ React.createElement("span", {
        className: "feed-icons",
        "aria-hidden": true
    }, icons.map(({ Icon, key })=>/*#__PURE__*/ React.createElement(Icon, {
            key: key,
            size: 14
        })));
    if (parts.length === 0) return null;
    if (only && isToolUIPart(only) && (isMutatingTool(getToolName(only)) || isClientReadTool(getToolName(only)))) {
        return /*#__PURE__*/ React.createElement(ToolPart, {
            part: only,
            partIndex: parts[0].index,
            ctx: ctx
        });
    }
    return /*#__PURE__*/ React.createElement(Feed, null, /*#__PURE__*/ React.createElement(Trigger, {
        type: "button",
        "aria-expanded": isOpen,
        "aria-controls": isOpen ? listId : undefined,
        onClick: ()=>setIsOpen((open)=>!open)
    }, /*#__PURE__*/ React.createElement(StepChevron, {
        size: 14
    }), leading, /*#__PURE__*/ React.createElement("span", {
        className: "feed-summary"
    }, isActive && /*#__PURE__*/ React.createElement(React.Fragment, null, t("chat.activity.working"), /*#__PURE__*/ React.createElement(Mid, null)), singleLabel ?? t("chat.activity.steps", {
        count: parts.length
    }), errorCount > 0 && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Mid, null), /*#__PURE__*/ React.createElement("span", {
        className: "feed-failed"
    }, t("chat.activity.failedCount", {
        count: errorCount
    }))), elapsed !== null && elapsed > 0 && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Mid, null), /*#__PURE__*/ React.createElement("span", {
        className: "feed-time"
    }, formatDuration(elapsed))))), isOpen && rail.isMounted && /*#__PURE__*/ React.createElement(Rail, {
        id: listId,
        "data-visible": rail.isVisible && isOpen
    }, parts.map(({ part, index })=>part.type === "reasoning" ? /*#__PURE__*/ React.createElement(ReasoningPart, {
            key: index,
            part: part,
            partIndex: index,
            ctx: ctx
        }) : /*#__PURE__*/ React.createElement(ToolPart, {
            key: index,
            part: part,
            partIndex: index,
            ctx: ctx
        }))));
}
